import type { CurvaDocument } from "@/document/model/document";
import { getAllComponents, getSceneNodeChildren } from "@/document/model/document";
import type { AutoLayoutMode, ComponentInstanceNode, ComponentNode, ComponentVariantNode, FlexChildLayout, FrameFlexLayout, FrameMirrorLayout, FrameNode, FrameRepeatLayout, GroupNode, NodeConstraints, SceneNode, Transform } from "@/document/model/nodes";
import { createDefaultFrameFlexLayout, createDefaultFrameMirrorLayout, createDefaultFrameRepeatLayout, createIdentityTransform } from "@/document/model/nodes";
import type { PrimitiveDefinition } from "@/document/model/primitives";
import type { NodeStyle } from "@/document/model/styles";
import { createDefaultStyle } from "@/document/model/styles";
import type { RichTextModel } from "@/document/model/text";
import { refreshBooleanNodes, refreshBooleanNodesForChangedNodes } from "@/document/operations/booleanTree";
import { applyAutoLayoutToDocument } from "@/document/operations/autoLayout";
import { cloneSceneNodeWithFreshIds } from "@/document/operations/cloneSceneNode";
import { componentVariants, createInstanceFromComponent, refreshComponentInstances } from "@/document/operations/componentSystem";
import { isComponentInstanceDropParent } from "@/document/operations/nodeLocking";
import { reparentNodePreserveWorldAppearance } from "@/document/operations/nodeWorldTransform";
import { flipSceneNodeInWorld, type FlipAxis } from "@/document/operations/flipNode";
import { getVectorNetworkBounds } from "@/document/operations/vectorGeometry";
import { applyBoundsToNode } from "@/document/operations/resizeNode";
import { refreshTextIntrinsicBounds } from "@/document/operations/textAutoSize";
import { prepareBakedFrameChildren, sceneNodeTreeIds } from "@/document/animation/bakeAnimationFrame";
import { createId } from "@/lib/ids";
import type { Rect } from "@/lib/math/rect";
import { transformRotationOrigin } from "@/lib/math/rotation";
import type { Command, CommandContext, CommandResult } from "./Command";

const COMPONENT_VARIANT_GAP = 40;

export class InsertNodeCommand implements Command {
  readonly id = "node.insert";
  readonly label = "Insert Node";

  constructor(
    private readonly parentId: string,
    private readonly node: SceneNode,
    private readonly index?: number,
  ) {}

  execute(context: CommandContext): CommandResult {
    const children = requireParentChildren(context.document, { type: "unknown", id: this.parentId });
    const insertionIndex = this.index ?? children.length;
    children.splice(insertionIndex, 0, structuredClone(this.node));
    finalizeDocumentGeometry(context.document, [this.node.id, this.parentId]);
    return this.result();
  }

  undo(context: CommandContext): CommandResult {
    const children = requireParentChildren(context.document, { type: "unknown", id: this.parentId });
    const nodeIndex = children.findIndex(node => node.id === this.node.id);
    if (nodeIndex === -1) {
      throw new Error(`Cannot undo insert; node ${this.node.id} is missing.`);
    }

    children.splice(nodeIndex, 1);
    finalizeDocumentGeometry(context.document, [this.node.id, this.parentId]);
    return this.result();
  }

  redo(context: CommandContext): CommandResult {
    return this.execute(context);
  }

  private result(): CommandResult {
    return {
      changedNodeIds: [this.node.id],
      affectedBounds: [this.node.bounds],
    };
  }
}

export class MoveNodeCommand implements Command {
  readonly id = "node.move";
  readonly label = "Move Node";

  constructor(
    private readonly nodeId: string,
    private readonly fromBounds: Rect,
    private readonly toBounds: Rect,
  ) {}

  execute(context: CommandContext): CommandResult {
    return this.applyBounds(context, this.toBounds);
  }

  undo(context: CommandContext): CommandResult {
    return this.applyBounds(context, this.fromBounds);
  }

  redo(context: CommandContext): CommandResult {
    return this.execute(context);
  }

  private applyBounds(context: CommandContext, bounds: Rect): CommandResult {
    const node = requireSceneNode(context.document, this.nodeId);
    applyBoundsToNode(node, bounds);
    finalizeDocumentGeometry(context.document, [this.nodeId]);
    return {
      changedNodeIds: [this.nodeId],
      affectedBounds: [this.fromBounds, this.toBounds],
      previewCompatible: true,
    };
  }
}

export class UpdateNodeBoundsCommand implements Command {
  readonly id = "node.updateBounds";
  readonly label = "Update Bounds";

  constructor(
    private readonly nodeId: string,
    private readonly fromBounds: Rect,
    private readonly toBounds: Rect,
    private readonly options: { ignoreChildConstraints?: boolean } = {},
  ) {}

  execute(context: CommandContext): CommandResult {
    return this.applyBounds(context, this.toBounds);
  }

  undo(context: CommandContext): CommandResult {
    return this.applyBounds(context, this.fromBounds);
  }

  redo(context: CommandContext): CommandResult {
    return this.execute(context);
  }

  canMergeWith(command: Command): boolean {
    // Coalesce continuous nudges or size scrubs, but never fold a move into a
    // resize (or vice versa) — those must stay separate undo steps.
    return command instanceof UpdateNodeBoundsCommand
      && command.nodeId === this.nodeId
      && boundsChangeKind(this.fromBounds, this.toBounds) === boundsChangeKind(command.fromBounds, command.toBounds);
  }

  merge(command: Command): Command {
    if (!(command instanceof UpdateNodeBoundsCommand)) {
      return this;
    }

    return new UpdateNodeBoundsCommand(this.nodeId, this.fromBounds, command.toBounds, {
      ignoreChildConstraints: command.options.ignoreChildConstraints ?? this.options.ignoreChildConstraints,
    });
  }

  private applyBounds(context: CommandContext, bounds: Rect): CommandResult {
    const node = requireSceneNode(context.document, this.nodeId);
    applyBoundsToNode(node, bounds, this.options);
    finalizeDocumentGeometry(context.document, [this.nodeId]);
    return {
      changedNodeIds: [this.nodeId],
      affectedBounds: [this.fromBounds, this.toBounds],
      previewCompatible: true,
    };
  }
}

/** Position-only vs size-affecting — used to keep move/resize as separate undos. */
function boundsChangeKind(from: Rect, to: Rect): "translate" | "resize" {
  const sizeChanged = Math.abs(from.width - to.width) > 1e-6
    || Math.abs(from.height - to.height) > 1e-6;
  return sizeChanged ? "resize" : "translate";
}

export class UpdateNodeTransformCommand implements Command {
  readonly id = "node.updateTransform";
  readonly label = "Update Transform";

  constructor(
    private readonly nodeId: string,
    private readonly fromTransform: Transform,
    private readonly toTransform: Transform,
  ) {}

  execute(context: CommandContext): CommandResult {
    return this.applyTransform(context, this.toTransform);
  }

  undo(context: CommandContext): CommandResult {
    return this.applyTransform(context, this.fromTransform);
  }

  redo(context: CommandContext): CommandResult {
    return this.execute(context);
  }

  canMergeWith(command: Command): boolean {
    return command instanceof UpdateNodeTransformCommand && command.nodeId === this.nodeId;
  }

  merge(command: Command): Command {
    if (!(command instanceof UpdateNodeTransformCommand)) {
      return this;
    }

    return new UpdateNodeTransformCommand(this.nodeId, this.fromTransform, command.toTransform);
  }

  private applyTransform(context: CommandContext, transform: Transform): CommandResult {
    const node = requireSceneNode(context.document, this.nodeId);
    node.transform = structuredClone(transform);
    finalizeDocumentGeometry(context.document, [this.nodeId]);
    return {
      changedNodeIds: [this.nodeId],
      affectedBounds: [node.bounds],
      previewCompatible: true,
    };
  }
}

export class FlipNodesCommand implements Command {
  readonly id = "node.flip";
  readonly label: string;

  constructor(
    private readonly nodeIds: string[],
    private readonly referenceBounds: Rect,
    private readonly axis: FlipAxis,
  ) {
    this.label = axis === "horizontal" ? "Flip Horizontal" : "Flip Vertical";
  }

  execute(context: CommandContext): CommandResult {
    return this.applyFlip(context);
  }

  undo(context: CommandContext): CommandResult {
    return this.applyFlip(context);
  }

  redo(context: CommandContext): CommandResult {
    return this.applyFlip(context);
  }

  private applyFlip(context: CommandContext): CommandResult {
    const affectedBounds: Rect[] = [this.referenceBounds];
    for (const nodeId of this.nodeIds) {
      const node = requireSceneNode(context.document, nodeId);
      affectedBounds.push(node.bounds);
      flipSceneNodeInWorld(context.document, nodeId, this.referenceBounds, this.axis);
      affectedBounds.push(node.bounds);
    }

    finalizeDocumentGeometry(context.document, this.nodeIds);
    return {
      changedNodeIds: this.nodeIds,
      affectedBounds,
    };
  }
}

export class UpdateNodeStyleCommand implements Command {
  readonly id = "node.updateStyle";
  readonly label = "Update Style";

  constructor(
    private readonly nodeId: string,
    private readonly fromStyle: NodeStyle,
    private readonly toStyle: NodeStyle,
  ) {}

  execute(context: CommandContext): CommandResult {
    return this.applyStyle(context, this.toStyle);
  }

  undo(context: CommandContext): CommandResult {
    return this.applyStyle(context, this.fromStyle);
  }

  redo(context: CommandContext): CommandResult {
    return this.execute(context);
  }

  canMergeWith(command: Command): boolean {
    return command instanceof UpdateNodeStyleCommand
      && command.nodeId === this.nodeId
      // Coalesce continuous tweaks (color/opacity scrubbing), but keep add/remove/
      // duplicate of fills, strokes, or effects as separate undo steps.
      && !styleStackMembershipChanged(this.fromStyle, this.toStyle)
      && !styleStackMembershipChanged(command.fromStyle, command.toStyle);
  }

  merge(command: Command): Command {
    if (!(command instanceof UpdateNodeStyleCommand)) {
      return this;
    }

    return new UpdateNodeStyleCommand(this.nodeId, this.fromStyle, command.toStyle);
  }

  private applyStyle(context: CommandContext, style: NodeStyle): CommandResult {
    const node = requireSceneNode(context.document, this.nodeId);
    node.style = structuredClone(style);
    finalizeDocumentGeometry(context.document, [this.nodeId]);
    return {
      changedNodeIds: [this.nodeId],
      affectedBounds: [node.bounds],
    };
  }
}

function styleStackMembershipChanged(from: NodeStyle, to: NodeStyle): boolean {
  if (from.fills.length !== to.fills.length) {
    return true;
  }

  const fromEffects = from.effects ?? [];
  const toEffects = to.effects ?? [];
  if (fromEffects.length !== toEffects.length) {
    return true;
  }

  const fromStrokes = from.strokes ?? (from.stroke ? [from.stroke] : []);
  const toStrokes = to.strokes ?? (to.stroke ? [to.stroke] : []);
  return fromStrokes.length !== toStrokes.length;
}

export class UpdatePrimitiveDefinitionCommand implements Command {
  readonly id = "node.updatePrimitive";
  readonly label = "Update Primitive";

  constructor(
    private readonly nodeId: string,
    private readonly fromPrimitive: PrimitiveDefinition,
    private readonly toPrimitive: PrimitiveDefinition,
  ) {}

  execute(context: CommandContext): CommandResult {
    return this.applyPrimitive(context, this.toPrimitive);
  }

  undo(context: CommandContext): CommandResult {
    return this.applyPrimitive(context, this.fromPrimitive);
  }

  redo(context: CommandContext): CommandResult {
    return this.execute(context);
  }

  canMergeWith(command: Command): boolean {
    return command instanceof UpdatePrimitiveDefinitionCommand && command.nodeId === this.nodeId;
  }

  merge(command: Command): Command {
    if (!(command instanceof UpdatePrimitiveDefinitionCommand)) {
      return this;
    }

    return new UpdatePrimitiveDefinitionCommand(this.nodeId, this.fromPrimitive, command.toPrimitive);
  }

  private applyPrimitive(context: CommandContext, primitive: PrimitiveDefinition): CommandResult {
    const node = requireSceneNode(context.document, this.nodeId);
    if (node.type !== "primitive") {
      throw new Error(`Node ${this.nodeId} is not a primitive.`);
    }

    node.primitive = structuredClone(primitive);
    finalizeDocumentGeometry(context.document, [this.nodeId]);
    return {
      changedNodeIds: [this.nodeId],
      affectedBounds: [node.bounds],
    };
  }
}

export class UpdateTextCommand implements Command {
  readonly id = "text.update";
  readonly label = "Update Text";

  constructor(
    private readonly nodeId: string,
    private readonly fromText: RichTextModel,
    private readonly toText: RichTextModel,
  ) {}

  execute(context: CommandContext): CommandResult {
    return this.applyText(context, this.toText);
  }

  undo(context: CommandContext): CommandResult {
    return this.applyText(context, this.fromText);
  }

  redo(context: CommandContext): CommandResult {
    return this.execute(context);
  }

  canMergeWith(command: Command): boolean {
    return command instanceof UpdateTextCommand && command.nodeId === this.nodeId;
  }

  merge(command: Command): Command {
    if (!(command instanceof UpdateTextCommand)) {
      return this;
    }

    return new UpdateTextCommand(this.nodeId, this.fromText, command.toText);
  }

  private applyText(context: CommandContext, text: RichTextModel): CommandResult {
    const node = requireSceneNode(context.document, this.nodeId);
    if (node.type !== "text") {
      throw new Error(`Node ${this.nodeId} is not a text node.`);
    }

    node.text = structuredClone(text);
    finalizeDocumentGeometry(context.document, [this.nodeId]);
    return {
      changedNodeIds: [this.nodeId],
      affectedBounds: [node.bounds],
    };
  }
}

export class UpdateFrameClipsContentCommand implements Command {
  readonly id = "frame.updateClipsContent";
  readonly label = "Update Frame Clip";

  constructor(
    private readonly frameId: string,
    private readonly fromClipsContent: boolean,
    private readonly toClipsContent: boolean,
  ) {}

  execute(context: CommandContext): CommandResult {
    return this.applyClipsContent(context, this.toClipsContent);
  }

  undo(context: CommandContext): CommandResult {
    return this.applyClipsContent(context, this.fromClipsContent);
  }

  redo(context: CommandContext): CommandResult {
    return this.execute(context);
  }

  private applyClipsContent(context: CommandContext, clipsContent: boolean): CommandResult {
    const node = requireSceneNode(context.document, this.frameId);
    if (node.type !== "frame") {
      throw new Error(`Node ${this.frameId} is not a frame.`);
    }

    node.clipsContent = clipsContent;
    finalizeDocumentGeometry(context.document, [this.frameId]);
    return {
      changedNodeIds: [this.frameId],
      affectedBounds: [node.bounds],
    };
  }
}

export class UpdateFrameCornerRadiiCommand implements Command {
  readonly id = "frame.updateCornerRadii";
  readonly label = "Update Frame Corners";

  constructor(
    private readonly frameId: string,
    private readonly fromCornerRadii: [number, number, number, number],
    private readonly toCornerRadii: [number, number, number, number],
  ) {}

  execute(context: CommandContext): CommandResult {
    return this.applyCornerRadii(context, this.toCornerRadii);
  }

  undo(context: CommandContext): CommandResult {
    return this.applyCornerRadii(context, this.fromCornerRadii);
  }

  redo(context: CommandContext): CommandResult {
    return this.execute(context);
  }

  canMergeWith(command: Command): boolean {
    return command instanceof UpdateFrameCornerRadiiCommand && command.frameId === this.frameId;
  }

  merge(command: Command): Command {
    if (!(command instanceof UpdateFrameCornerRadiiCommand)) {
      return this;
    }

    return new UpdateFrameCornerRadiiCommand(this.frameId, this.fromCornerRadii, command.toCornerRadii);
  }

  private applyCornerRadii(context: CommandContext, cornerRadii: [number, number, number, number]): CommandResult {
    const node = requireSceneNode(context.document, this.frameId);
    if (node.type !== "frame") {
      throw new Error(`Node ${this.frameId} is not a frame.`);
    }

    const maxRadius = Math.min(Math.abs(node.bounds.width), Math.abs(node.bounds.height)) / 2;
    node.cornerRadii = cornerRadii.map(radius => Math.max(0, Math.min(maxRadius, radius))) as [number, number, number, number];
    finalizeDocumentGeometry(context.document, [this.frameId]);
    return {
      changedNodeIds: [this.frameId],
      affectedBounds: [node.bounds],
    };
  }
}

export interface FrameLayoutSettings {
  autoLayout: AutoLayoutMode;
  flexLayout: FrameFlexLayout;
  repeatLayout: FrameRepeatLayout;
  mirrorLayout: FrameMirrorLayout;
}

export class UpdateFrameLayoutCommand implements Command {
  readonly id = "frame.updateLayout";
  readonly label = "Update Frame Layout";

  constructor(
    private readonly frameId: string,
    private readonly fromLayout: FrameLayoutSettings,
    private readonly toLayout: FrameLayoutSettings,
  ) {}

  execute(context: CommandContext): CommandResult {
    return this.applyLayout(context, this.toLayout);
  }

  undo(context: CommandContext): CommandResult {
    return this.applyLayout(context, this.fromLayout);
  }

  redo(context: CommandContext): CommandResult {
    return this.execute(context);
  }

  canMergeWith(command: Command): boolean {
    return command instanceof UpdateFrameLayoutCommand && command.frameId === this.frameId;
  }

  merge(command: Command): Command {
    if (!(command instanceof UpdateFrameLayoutCommand)) {
      return this;
    }

    return new UpdateFrameLayoutCommand(this.frameId, this.fromLayout, command.toLayout);
  }

  private applyLayout(context: CommandContext, layout: FrameLayoutSettings): CommandResult {
    const node = requireSceneNode(context.document, this.frameId);
    if (node.type !== "frame") {
      throw new Error(`Node ${this.frameId} is not a frame.`);
    }

    const affectedBounds = [node.bounds, ...node.children.map(child => child.bounds)];
    node.autoLayout = layout.autoLayout;
    node.flexLayout = structuredClone(layout.flexLayout);
    node.repeatLayout = structuredClone(layout.repeatLayout);
    node.mirrorLayout = structuredClone(layout.mirrorLayout);
    finalizeDocumentGeometry(context.document, [this.frameId]);
    return {
      changedNodeIds: [this.frameId, ...node.children.map(child => child.id)],
      affectedBounds,
    };
  }
}

export class UpdateFlexChildLayoutCommand implements Command {
  readonly id = "node.updateFlexChild";
  readonly label = "Update Flex Child";

  constructor(
    private readonly nodeId: string,
    private readonly fromLayout: FlexChildLayout | undefined,
    private readonly toLayout: FlexChildLayout,
  ) {}

  execute(context: CommandContext): CommandResult {
    return this.applyLayout(context, this.toLayout);
  }

  undo(context: CommandContext): CommandResult {
    return this.applyLayout(context, this.fromLayout);
  }

  redo(context: CommandContext): CommandResult {
    return this.execute(context);
  }

  canMergeWith(command: Command): boolean {
    return command instanceof UpdateFlexChildLayoutCommand && command.nodeId === this.nodeId;
  }

  merge(command: Command): Command {
    if (!(command instanceof UpdateFlexChildLayoutCommand)) {
      return this;
    }

    return new UpdateFlexChildLayoutCommand(this.nodeId, this.fromLayout, command.toLayout);
  }

  private applyLayout(context: CommandContext, layout: FlexChildLayout | undefined): CommandResult {
    const node = requireSceneNode(context.document, this.nodeId);
    const affectedBounds = [node.bounds];
    node.flexChild = layout ? structuredClone(layout) : undefined;
    finalizeDocumentGeometry(context.document, [this.nodeId]);
    return {
      changedNodeIds: [this.nodeId],
      affectedBounds,
    };
  }
}

export class UpdateNodeConstraintsCommand implements Command {
  readonly id = "node.updateConstraints";
  readonly label = "Update Constraints";

  constructor(
    private readonly nodeId: string,
    private readonly fromConstraints: NodeConstraints | undefined,
    private readonly toConstraints: NodeConstraints,
  ) {}

  execute(context: CommandContext): CommandResult {
    return this.applyConstraints(context, this.toConstraints);
  }

  undo(context: CommandContext): CommandResult {
    return this.applyConstraints(context, this.fromConstraints);
  }

  redo(context: CommandContext): CommandResult {
    return this.execute(context);
  }

  private applyConstraints(context: CommandContext, constraints: NodeConstraints | undefined): CommandResult {
    const node = requireSceneNode(context.document, this.nodeId);
    node.constraints = constraints ? structuredClone(constraints) : undefined;
    return {
      changedNodeIds: [this.nodeId],
      affectedBounds: [node.bounds],
    };
  }
}

export class RenameNodeCommand implements Command {
  readonly id = "node.rename";
  readonly label = "Rename Layer";

  constructor(
    private readonly nodeId: string,
    private readonly fromName: string,
    private readonly toName: string,
    private readonly fromNameManuallyEdited = false,
    private readonly toNameManuallyEdited = true,
  ) {}

  execute(context: CommandContext): CommandResult {
    return this.applyName(context, this.toName);
  }

  undo(context: CommandContext): CommandResult {
    return this.applyName(context, this.fromName);
  }

  redo(context: CommandContext): CommandResult {
    return this.execute(context);
  }

  private applyName(context: CommandContext, name: string): CommandResult {
    const node = requireSceneNode(context.document, this.nodeId);
    node.name = name;
    node.nameManuallyEdited = name === this.toName
      ? this.toNameManuallyEdited
      : this.fromNameManuallyEdited;
    return {
      changedNodeIds: [this.nodeId],
      affectedBounds: [node.bounds],
    };
  }
}

export class UpdateNodeStateCommand implements Command {
  readonly id = "node.updateState";
  readonly label = "Update Layer State";

  constructor(
    private readonly nodeId: string,
    private readonly fromState: Partial<Pick<SceneNode, "visible" | "locked">>,
    private readonly toState: Partial<Pick<SceneNode, "visible" | "locked">>,
  ) {}

  execute(context: CommandContext): CommandResult {
    return this.applyState(context, this.toState);
  }

  undo(context: CommandContext): CommandResult {
    return this.applyState(context, this.fromState);
  }

  redo(context: CommandContext): CommandResult {
    return this.execute(context);
  }

  private applyState(context: CommandContext, state: Partial<Pick<SceneNode, "visible" | "locked">>): CommandResult {
    const node = requireSceneNode(context.document, this.nodeId);
    if (state.visible !== undefined) {
      node.visible = state.visible;
    }
    if (state.locked !== undefined) {
      node.locked = state.locked;
    }
    finalizeDocumentGeometry(context.document, [this.nodeId]);
    return {
      changedNodeIds: [this.nodeId],
      affectedBounds: [node.bounds],
    };
  }
}

export class DeleteNodeCommand implements Command {
  readonly id = "node.delete";
  readonly label = "Delete Node";
  private deletedNode?: SceneNode;
  private deletedIndex?: number;
  private parent?: SceneNodeParent;

  constructor(private readonly nodeId: string) {}

  execute(context: CommandContext): CommandResult {
    const removal = removeSceneNode(context.document, this.nodeId);
    this.deletedNode = removal.node;
    this.deletedIndex = removal.index;
    this.parent = removal.parent;
    finalizeDocumentGeometry(context.document, [this.nodeId, removal.parent.id]);
    return {
      changedNodeIds: [this.nodeId],
      affectedBounds: [removal.node.bounds],
    };
  }

  undo(context: CommandContext): CommandResult {
    if (!this.deletedNode || this.deletedIndex === undefined || !this.parent) {
      throw new Error(`Cannot undo delete; node ${this.nodeId} was not recorded.`);
    }

    const children = requireParentChildren(context.document, this.parent);
    children.splice(this.deletedIndex, 0, structuredClone(this.deletedNode));
    finalizeDocumentGeometry(context.document, [this.nodeId, this.parent.id]);
    return {
      changedNodeIds: [this.nodeId],
      affectedBounds: [this.deletedNode.bounds],
    };
  }

  redo(context: CommandContext): CommandResult {
    return this.execute(context);
  }
}

export interface NodeTreeDropTarget {
  parentId: string;
  parentType: "page" | "frame" | "group" | "boolean" | "component" | "componentVariant" | "instance";
  index: number;
}

export class MoveNodesInTreeCommand implements Command {
  readonly id = "node.moveInTree";
  readonly label = "Move Layers";
  private beforePages?: CurvaDocument["pages"];
  private afterPages?: CurvaDocument["pages"];

  constructor(
    private readonly nodeIds: string[],
    private readonly target: NodeTreeDropTarget,
  ) {}

  execute(context: CommandContext): CommandResult {
    const normalizedNodeIds = normalizeTopLevelNodeIds(context.document, this.nodeIds);
    if (normalizedNodeIds.length === 0) {
      throw new Error("Cannot move an empty layer selection.");
    }

    this.beforePages = structuredClone(context.document.pages);
    moveNodesInTree(context.document, normalizedNodeIds, this.target);
    normalizeTopLevelMasks(context.document);
    this.afterPages = structuredClone(context.document.pages);
    finalizeDocumentGeometry(context.document, [...normalizedNodeIds, this.target.parentId]);
    return {
      changedNodeIds: [...normalizedNodeIds, this.target.parentId],
      affectedBounds: selectedNodeBounds(context.document, normalizedNodeIds),
    };
  }

  undo(context: CommandContext): CommandResult {
    if (!this.beforePages) {
      throw new Error("Cannot undo layer move; original tree is missing.");
    }

    context.document.pages = structuredClone(this.beforePages);
    finalizeDocumentGeometry(context.document, this.nodeIds);
    return {
      changedNodeIds: this.nodeIds,
      affectedBounds: selectedNodeBounds(context.document, this.nodeIds),
    };
  }

  redo(context: CommandContext): CommandResult {
    if (!this.afterPages) {
      return this.execute(context);
    }

    context.document.pages = structuredClone(this.afterPages);
    finalizeDocumentGeometry(context.document, this.nodeIds);
    return {
      changedNodeIds: this.nodeIds,
      affectedBounds: selectedNodeBounds(context.document, this.nodeIds),
    };
  }
}

export class UpdateNodeMaskCommand implements Command {
  readonly id = "node.updateMask";
  readonly label: string;
  private beforePages?: CurvaDocument["pages"];
  private afterPages?: CurvaDocument["pages"];

  constructor(
    private readonly nodeId: string,
    private readonly isMask: boolean,
    private readonly groupId = createId("group"),
  ) {
    this.label = isMask ? "Use as Mask" : "Remove Mask";
  }

  execute(context: CommandContext): CommandResult {
    this.beforePages = structuredClone(context.document.pages);
    const node = requireSceneNode(context.document, this.nodeId);
    if (this.isMask) {
      node.isMask = true;
      ensureMaskHasContainerParent(context.document, this.nodeId, this.groupId);
    } else {
      delete node.isMask;
    }
    this.afterPages = structuredClone(context.document.pages);
    const updated = requireSceneNode(context.document, this.nodeId);
    finalizeDocumentGeometry(context.document, [this.nodeId, this.groupId]);
    return {
      changedNodeIds: [this.nodeId, this.groupId],
      affectedBounds: [updated.bounds],
    };
  }

  undo(context: CommandContext): CommandResult {
    if (!this.beforePages) {
      throw new Error("Cannot undo mask change; original tree is missing.");
    }
    context.document.pages = structuredClone(this.beforePages);
    finalizeDocumentGeometry(context.document, [this.nodeId, this.groupId]);
    return {
      changedNodeIds: [this.nodeId, this.groupId],
      affectedBounds: selectedNodeBounds(context.document, [this.nodeId]),
    };
  }

  redo(context: CommandContext): CommandResult {
    if (!this.afterPages) {
      return this.execute(context);
    }
    context.document.pages = structuredClone(this.afterPages);
    finalizeDocumentGeometry(context.document, [this.nodeId, this.groupId]);
    return {
      changedNodeIds: [this.nodeId, this.groupId],
      affectedBounds: selectedNodeBounds(context.document, [this.nodeId]),
    };
  }
}

export class GroupNodesCommand implements Command {
  readonly id: string;
  readonly label: string;
  private snapshot?: GroupSnapshot;

  constructor(
    private readonly nodeIds: string[],
    private readonly groupId = createId("group"),
    private readonly containerType: "group" | "frame" = "group",
  ) {
    this.id = containerType === "frame" ? "node.frame" : "node.group";
    this.label = containerType === "frame" ? "Frame Nodes" : "Group Nodes";
  }

  execute(context: CommandContext): CommandResult {
    const grouped = groupNodes(context.document, this.nodeIds, this.groupId, this.containerType);
    finalizeDocumentGeometry(context.document, [this.groupId, ...grouped.nodeIds, grouped.parent.id]);
    this.snapshot = grouped;
    return {
      changedNodeIds: [this.groupId, ...grouped.nodeIds],
      affectedBounds: [grouped.group.bounds],
    };
  }

  undo(context: CommandContext): CommandResult {
    if (!this.snapshot) {
      throw new Error("Cannot undo group; group snapshot is missing.");
    }

    const children = requireParentChildren(context.document, this.snapshot.parent);
    const groupIndex = children.findIndex(node => node.id === this.groupId);
    if (groupIndex === -1) {
      throw new Error(`Cannot undo group; group ${this.groupId} is missing.`);
    }

    children.splice(groupIndex, 1);
    for (const location of [...this.snapshot.nodeLocations].sort((left, right) => parentKey(left.parent).localeCompare(parentKey(right.parent)) || left.index - right.index)) {
      const parentChildren = requireParentChildren(context.document, location.parent);
      parentChildren.splice(Math.min(location.index, parentChildren.length), 0, structuredClone(location.node));
    }
    finalizeDocumentGeometry(context.document, [this.groupId, ...this.snapshot.nodeIds, ...this.snapshot.nodeLocations.map(location => location.parent.id), this.snapshot.parent.id]);
    return {
      changedNodeIds: [this.groupId, ...this.snapshot.nodeIds],
      affectedBounds: [this.snapshot.group.bounds],
    };
  }

  redo(context: CommandContext): CommandResult {
    return this.execute(context);
  }
}

export class CreateComponentCommand implements Command {
  readonly id = "node.createComponent";
  readonly label = "Create Component";
  private snapshot?: ComponentSnapshot;

  constructor(
    private readonly nodeIds: string[],
    private readonly componentId = createId("component"),
  ) {}

  execute(context: CommandContext): CommandResult {
    const component = createComponentFromNodes(context.document, this.nodeIds, this.componentId);
    finalizeDocumentGeometry(context.document, [this.componentId, ...component.nodeIds, component.parent.id]);
    this.snapshot = component;
    return {
      changedNodeIds: [this.componentId, ...component.nodeIds],
      affectedBounds: [component.component.bounds],
    };
  }

  undo(context: CommandContext): CommandResult {
    if (!this.snapshot) {
      throw new Error("Cannot undo component creation; component snapshot is missing.");
    }

    const children = requireParentChildren(context.document, this.snapshot.parent);
    const componentIndex = children.findIndex(node => node.id === this.componentId);
    if (componentIndex === -1) {
      throw new Error(`Cannot undo component creation; component ${this.componentId} is missing.`);
    }

    children.splice(componentIndex, 1);
    for (const location of [...this.snapshot.nodeLocations].sort((left, right) => parentKey(left.parent).localeCompare(parentKey(right.parent)) || left.index - right.index)) {
      const parentChildren = requireParentChildren(context.document, location.parent);
      parentChildren.splice(Math.min(location.index, parentChildren.length), 0, structuredClone(location.node));
    }
    finalizeDocumentGeometry(context.document, [this.componentId, ...this.snapshot.nodeIds, ...this.snapshot.nodeLocations.map(location => location.parent.id), this.snapshot.parent.id]);
    return {
      changedNodeIds: [this.componentId, ...this.snapshot.nodeIds],
      affectedBounds: [this.snapshot.component.bounds],
    };
  }

  redo(context: CommandContext): CommandResult {
    return this.execute(context);
  }
}

export class AddComponentVariantCommand implements Command {
  readonly id = "component.addVariant";
  readonly label = "Add Component Variant";
  private before?: ComponentNode;
  private after?: ComponentNode;
  private variantId?: string;

  constructor(private readonly componentId: string) {}

  execute(context: CommandContext): CommandResult {
    const component = requireSceneNode(context.document, this.componentId);
    if (component.type !== "component") {
      throw new Error(`Node ${this.componentId} is not a component.`);
    }
    if (this.after) {
      Object.assign(component, structuredClone(this.after));
      finalizeDocumentGeometry(context.document, [this.componentId, this.variantId ?? this.componentId]);
      return {
        changedNodeIds: [this.componentId, this.variantId ?? this.componentId],
        affectedBounds: [component.bounds],
      };
    }

    this.before = structuredClone(component);
    const variants = componentVariants(component);
    const base = variants.at(-1);
    if (!base) {
      throw new Error("Component has no variant to duplicate.");
    }

    const nextVariant = cloneSceneNodeWithFreshIds(base, { renameRoot: false }) as ComponentVariantNode;
    nextVariant.name = `Variant ${variants.length + 1}`;
    this.variantId = nextVariant.id;
    component.children.push(nextVariant);
    finalizeDocumentGeometry(context.document, [component.id, nextVariant.id]);
    this.after = structuredClone(component);
    return {
      changedNodeIds: [component.id, nextVariant.id],
      affectedBounds: [component.bounds],
    };
  }

  undo(context: CommandContext): CommandResult {
    if (!this.before) {
      throw new Error("Cannot undo add variant; original component is missing.");
    }
    const component = requireSceneNode(context.document, this.componentId);
    if (component.type !== "component") {
      throw new Error(`Node ${this.componentId} is not a component.`);
    }
    Object.assign(component, structuredClone(this.before));
    finalizeDocumentGeometry(context.document, [this.componentId]);
    return {
      changedNodeIds: [this.componentId],
      affectedBounds: [component.bounds],
    };
  }

  redo(context: CommandContext): CommandResult {
    return this.execute(context);
  }
}

export class DeleteComponentVariantCommand implements Command {
  readonly id = "component.deleteVariant";
  readonly label = "Delete Component Variant";
  private before?: ComponentNode;

  constructor(
    private readonly componentId: string,
    private readonly variantId: string,
  ) {}

  execute(context: CommandContext): CommandResult {
    const component = requireSceneNode(context.document, this.componentId);
    if (component.type !== "component") {
      throw new Error(`Node ${this.componentId} is not a component.`);
    }
    const variants = componentVariants(component);
    if (variants.length <= 1) {
      throw new Error("A component needs at least one variant.");
    }

    const index = component.children.findIndex(variant => variant.id === this.variantId);
    if (index < 0) {
      throw new Error(`Variant ${this.variantId} was not found.`);
    }

    this.before = structuredClone(component);
    component.children.splice(index, 1);
    const fallbackVariant = component.children[0];
    for (const instance of getComponentInstances(context.document, component.id)) {
      if (instance.variantId === this.variantId && fallbackVariant) {
        instance.variantId = fallbackVariant.id;
        instance.overrides = {};
        instance.bounds = {
          ...instance.bounds,
          width: fallbackVariant.bounds.width,
          height: fallbackVariant.bounds.height,
        };
      }
    }
    finalizeDocumentGeometry(context.document, [component.id, this.variantId]);
    return {
      changedNodeIds: [component.id, this.variantId],
      affectedBounds: [component.bounds],
    };
  }

  undo(context: CommandContext): CommandResult {
    if (!this.before) {
      throw new Error("Cannot undo delete variant; original component is missing.");
    }
    const component = requireSceneNode(context.document, this.componentId);
    if (component.type !== "component") {
      throw new Error(`Node ${this.componentId} is not a component.`);
    }
    Object.assign(component, structuredClone(this.before));
    finalizeDocumentGeometry(context.document, [this.componentId]);
    return {
      changedNodeIds: [this.componentId],
      affectedBounds: [component.bounds],
    };
  }

  redo(context: CommandContext): CommandResult {
    return this.execute(context);
  }
}

export class UpdateInstanceVariantCommand implements Command {
  readonly id = "instance.updateVariant";
  readonly label = "Swap Instance Variant";
  private before?: Pick<ComponentInstanceNode, "variantId" | "overrides" | "bounds">;

  constructor(
    private readonly instanceId: string,
    private readonly variantId: string,
  ) {}

  execute(context: CommandContext): CommandResult {
    const instance = requireSceneNode(context.document, this.instanceId);
    if (instance.type !== "instance") {
      throw new Error(`Node ${this.instanceId} is not an instance.`);
    }
    const component = requireSceneNode(context.document, instance.componentId);
    const variant = component.type === "component" ? componentVariants(component).find(candidate => candidate.id === this.variantId) : undefined;
    if (!variant) {
      throw new Error(`Variant ${this.variantId} was not found.`);
    }

    this.before = {
      variantId: instance.variantId,
      overrides: structuredClone(instance.overrides),
      bounds: structuredClone(instance.bounds),
    };
    instance.variantId = this.variantId;
    instance.overrides = {};
    instance.bounds = {
      ...instance.bounds,
      width: variant.bounds.width,
      height: variant.bounds.height,
    };
    finalizeDocumentGeometry(context.document, [this.instanceId]);
    return {
      changedNodeIds: [this.instanceId],
      affectedBounds: [instance.bounds],
    };
  }

  undo(context: CommandContext): CommandResult {
    if (!this.before) {
      throw new Error("Cannot undo instance variant swap; original state is missing.");
    }
    const instance = requireSceneNode(context.document, this.instanceId);
    if (instance.type !== "instance") {
      throw new Error(`Node ${this.instanceId} is not an instance.`);
    }
    instance.variantId = this.before.variantId;
    instance.overrides = structuredClone(this.before.overrides);
    instance.bounds = structuredClone(this.before.bounds);
    finalizeDocumentGeometry(context.document, [this.instanceId]);
    return {
      changedNodeIds: [this.instanceId],
      affectedBounds: [instance.bounds],
    };
  }

  redo(context: CommandContext): CommandResult {
    return this.execute(context);
  }
}

export class DetachInstanceCommand implements Command {
  readonly id = "instance.detach";
  readonly label = "Detach Instance";
  private snapshot?: {
    parent: SceneNodeParent;
    index: number;
    instance: ComponentInstanceNode;
    group: GroupNode;
  };

  constructor(
    private readonly instanceId: string,
    private readonly groupId = createId("group"),
  ) {}

  execute(context: CommandContext): CommandResult {
    if (this.snapshot) {
      replaceNodeInParent(context.document, this.snapshot.parent, this.instanceId, structuredClone(this.snapshot.group));
      finalizeDocumentGeometry(context.document, [this.instanceId, this.groupId, this.snapshot.parent.id]);
      return {
        changedNodeIds: [this.instanceId, this.groupId],
        affectedBounds: [this.snapshot.instance.bounds, this.snapshot.group.bounds],
      };
    }

    const location = findNodeLocation(context.document, this.instanceId);
    if (!location || location.node.type !== "instance") {
      throw new Error(`Node ${this.instanceId} is not an instance.`);
    }

    const group: GroupNode = {
      id: this.groupId,
      name: location.node.name,
      type: "group",
      visible: location.node.visible,
      locked: location.node.locked,
      transform: structuredClone(location.node.transform),
      bounds: structuredClone(location.node.bounds),
      style: structuredClone(location.node.style),
      flexChild: location.node.flexChild ? structuredClone(location.node.flexChild) : undefined,
      children: location.node.children.map(child => detachedSceneNode(child)),
    };

    this.snapshot = {
      parent: location.parent,
      index: location.index,
      instance: structuredClone(location.node),
      group: structuredClone(group),
    };
    location.children.splice(location.index, 1, group);
    finalizeDocumentGeometry(context.document, [this.instanceId, this.groupId, location.parent.id]);
    return {
      changedNodeIds: [this.instanceId, this.groupId],
      affectedBounds: [location.node.bounds, group.bounds],
    };
  }

  undo(context: CommandContext): CommandResult {
    if (!this.snapshot) {
      throw new Error("Cannot undo detach instance; original instance is missing.");
    }

    replaceNodeInParent(context.document, this.snapshot.parent, this.groupId, structuredClone(this.snapshot.instance));
    finalizeDocumentGeometry(context.document, [this.instanceId, this.groupId, this.snapshot.parent.id]);
    return {
      changedNodeIds: [this.instanceId, this.groupId],
      affectedBounds: [this.snapshot.instance.bounds, this.snapshot.group.bounds],
    };
  }

  redo(context: CommandContext): CommandResult {
    return this.execute(context);
  }
}

export class UngroupNodesCommand implements Command {
  readonly id = "node.ungroup";
  readonly label = "Ungroup Nodes";
  private snapshots?: UngroupSnapshot[];

  constructor(private readonly nodeIds: string[]) {}

  execute(context: CommandContext): CommandResult {
    const snapshots = this.nodeIds.flatMap(nodeId => ungroupNode(context.document, nodeId));
    finalizeDocumentGeometry(context.document, snapshots.flatMap(snapshot => [snapshot.parent.id, snapshot.group.id, ...snapshot.group.children.map(child => child.id)]));
    this.snapshots = snapshots;
    return {
      changedNodeIds: snapshots.flatMap(snapshot => [snapshot.group.id, ...snapshot.group.children.map(child => child.id)]),
      affectedBounds: snapshots.map(snapshot => snapshot.group.bounds),
    };
  }

  undo(context: CommandContext): CommandResult {
    if (!this.snapshots) {
      throw new Error("Cannot undo ungroup; ungroup snapshot is missing.");
    }

    for (const snapshot of [...this.snapshots].reverse()) {
      const children = requireParentChildren(context.document, snapshot.parent);
      const childIds = new Set(snapshot.group.children.map(child => child.id));
      const firstChildIndex = children.findIndex(node => childIds.has(node.id));
      const removalStart = firstChildIndex >= 0 ? firstChildIndex : snapshot.index;
      children.splice(removalStart, snapshot.group.children.length, structuredClone(snapshot.group));
    }

    finalizeDocumentGeometry(context.document, this.snapshots.flatMap(snapshot => [snapshot.parent.id, snapshot.group.id, ...snapshot.group.children.map(child => child.id)]));
    return {
      changedNodeIds: this.snapshots.flatMap(snapshot => [snapshot.group.id, ...snapshot.group.children.map(child => child.id)]),
      affectedBounds: this.snapshots.map(snapshot => snapshot.group.bounds),
    };
  }

  redo(context: CommandContext): CommandResult {
    return this.execute(context);
  }
}

export class ConvertFrameGroupCommand implements Command {
  readonly id = "node.convertFrameGroup";
  readonly label = "Convert Frame/Group";
  private before?: SceneNode;
  private after?: SceneNode;
  private parent?: SceneNodeParent;

  constructor(
    private readonly nodeId: string,
    private readonly targetType: "frame" | "group",
  ) {}

  execute(context: CommandContext): CommandResult {
    if (this.after && this.parent) {
      replaceNodeInParent(context.document, this.parent, this.nodeId, structuredClone(this.after));
      finalizeDocumentGeometry(context.document, [this.nodeId, this.parent.id]);
      return {
        changedNodeIds: [this.nodeId],
        affectedBounds: [this.after.bounds],
      };
    }

    const location = findNodeLocation(context.document, this.nodeId);
    if (!location || (location.node.type !== "frame" && location.node.type !== "group")) {
      throw new Error(`Node ${this.nodeId} is not a frame or group.`);
    }
    if (location.node.type === this.targetType) {
      throw new Error(`Node ${this.nodeId} is already a ${this.targetType}.`);
    }

    this.before = structuredClone(location.node);
    this.parent = location.parent;
    const replacement = location.node.type === "group"
      ? groupToFrame(location.node)
      : frameToGroup(location.node);
    location.children.splice(location.index, 1, replacement);
    finalizeDocumentGeometry(context.document, [this.nodeId, location.parent.id, ...getSceneNodeChildren(replacement)!.map(child => child.id)]);
    this.after = structuredClone(findSceneNodeInDocument(context.document, this.nodeId) ?? replacement);
    return {
      changedNodeIds: [this.nodeId],
      affectedBounds: [this.before.bounds, this.after.bounds],
    };
  }

  undo(context: CommandContext): CommandResult {
    if (!this.before || !this.parent) {
      throw new Error("Cannot undo frame/group conversion; original node is missing.");
    }

    replaceNodeInParent(context.document, this.parent, this.nodeId, structuredClone(this.before));
    finalizeDocumentGeometry(context.document, [this.nodeId, this.parent.id]);
    return {
      changedNodeIds: [this.nodeId],
      affectedBounds: [this.before.bounds],
    };
  }

  redo(context: CommandContext): CommandResult {
    return this.execute(context);
  }
}

export type ReorderPlacement = "front" | "back" | "forward" | "backward";

export class ReorderNodesCommand implements Command {
  readonly id = "node.reorder";
  readonly label = "Reorder Nodes";
  private snapshots?: ParentOrderSnapshot[];

  constructor(
    private readonly nodeIds: string[],
    private readonly placement: ReorderPlacement,
  ) {}

  execute(context: CommandContext): CommandResult {
    const parents = findParentsWithNodes(context.document, new Set(this.nodeIds));
    this.snapshots = parents.map(({ parent, children }) => ({
      parent,
      beforeIds: children.map(node => node.id),
    }));

    for (const { children } of parents) {
      reorderChildren(children, new Set(this.nodeIds), this.placement);
    }
    finalizeDocumentGeometry(context.document, [...this.nodeIds, ...parents.map(parent => parent.parent.id)]);

    this.snapshots = (this.snapshots ?? []).map(snapshot => {
      const children = requireParentChildren(context.document, snapshot.parent);
      return {
        ...snapshot,
        afterIds: children.map(node => node.id),
      };
    });

    return {
      changedNodeIds: this.nodeIds,
      affectedBounds: selectedNodeBounds(context.document, this.nodeIds),
    };
  }

  undo(context: CommandContext): CommandResult {
    if (!this.snapshots) {
      throw new Error("Cannot undo reorder; original order is missing.");
    }

    for (const snapshot of this.snapshots) {
      restoreChildrenOrder(requireParentChildren(context.document, snapshot.parent), snapshot.beforeIds);
    }

    finalizeDocumentGeometry(context.document, [...this.nodeIds, ...this.snapshots.map(snapshot => snapshot.parent.id)]);
    return {
      changedNodeIds: this.nodeIds,
      affectedBounds: selectedNodeBounds(context.document, this.nodeIds),
    };
  }

  redo(context: CommandContext): CommandResult {
    if (!this.snapshots) {
      return this.execute(context);
    }

    for (const snapshot of this.snapshots) {
      restoreChildrenOrder(requireParentChildren(context.document, snapshot.parent), snapshot.afterIds ?? snapshot.beforeIds);
    }

    finalizeDocumentGeometry(context.document, [...this.nodeIds, ...this.snapshots.map(snapshot => snapshot.parent.id)]);
    return {
      changedNodeIds: this.nodeIds,
      affectedBounds: selectedNodeBounds(context.document, this.nodeIds),
    };
  }
}

export class ReplaceNodesCommand implements Command {
  readonly id = "node.replace";

  private snapshot?: ReplaceSnapshot;

  constructor(
    readonly label: string,
    private readonly nodeIds: string[],
    private readonly replacementNodes: SceneNode[],
  ) {}

  execute(context: CommandContext): CommandResult {
    const shared = findSharedParent(context.document, this.nodeIds);
    if (!shared) {
      throw new Error("Cannot replace nodes that do not share the same parent.");
    }

    const selected = new Set(this.nodeIds);
    const nodes = shared.children.filter(node => selected.has(node.id));
    const firstIndex = shared.children.findIndex(node => selected.has(node.id));
    if (nodes.length === 0 || firstIndex < 0) {
      throw new Error("Cannot replace an empty node selection.");
    }
    const insertionIndex = shared.children.slice(0, firstIndex).filter(node => !selected.has(node.id)).length;

    this.snapshot = {
      parent: shared.parent,
      children: shared.children.map(node => structuredClone(node)),
      replacementIds: this.replacementNodes.map(node => node.id),
    };

    shared.children.splice(0, shared.children.length, ...shared.children.filter(node => !selected.has(node.id)));
    shared.children.splice(insertionIndex, 0, ...this.replacementNodes.map(node => structuredClone(node)));
    finalizeDocumentGeometry(context.document, [...this.nodeIds, ...this.replacementNodes.map(node => node.id), shared.parent.id]);

    return {
      changedNodeIds: [...this.nodeIds, ...this.replacementNodes.map(node => node.id)],
      affectedBounds: [...nodes.map(node => node.bounds), ...this.replacementNodes.map(node => node.bounds)],
    };
  }

  undo(context: CommandContext): CommandResult {
    if (!this.snapshot) {
      throw new Error("Cannot undo replace; original nodes are missing.");
    }

    const children = requireParentChildren(context.document, this.snapshot.parent);
    children.splice(0, children.length, ...this.snapshot.children.map(node => structuredClone(node)));
    finalizeDocumentGeometry(context.document, [...this.nodeIds, ...this.snapshot.replacementIds, this.snapshot.parent.id]);

    return {
      changedNodeIds: [...this.nodeIds, ...this.replacementNodes.map(node => node.id)],
      affectedBounds: [...this.snapshot.children.map(node => node.bounds), ...this.replacementNodes.map(node => node.bounds)],
    };
  }

  redo(context: CommandContext): CommandResult {
    return this.execute(context);
  }
}

export class BakeAnimationFrameCommand implements Command {
  readonly id = "animation.bakeFrame";
  readonly label = "Bake animation";

  private beforeChildren?: SceneNode[];
  private beforeChildIds: string[] = [];
  private afterChildIds: string[] = [];

  constructor(
    private readonly frameId: string,
    private readonly timeMs: number,
    private readonly rootNodeId?: string,
  ) {}

  execute(context: CommandContext): CommandResult {
    const frame = requireSceneNode(context.document, this.frameId);
    if (frame.type !== "frame") {
      throw new Error(`Node ${this.frameId} is not a frame.`);
    }

    this.beforeChildren = frame.children.map(child => structuredClone(child));
    this.beforeChildIds = sceneNodeTreeIds(this.beforeChildren);
    const bakedChildren = prepareBakedFrameChildren(context.document, this.frameId, this.timeMs, this.rootNodeId);
    this.afterChildIds = sceneNodeTreeIds(bakedChildren);
    frame.children.splice(0, frame.children.length, ...bakedChildren.map(child => structuredClone(child)));
    finalizeDocumentGeometry(context.document, [this.frameId, ...this.beforeChildIds, ...this.afterChildIds]);

    return {
      changedNodeIds: [...new Set([this.frameId, ...this.beforeChildIds, ...this.afterChildIds])],
      affectedBounds: [frame.bounds],
    };
  }

  undo(context: CommandContext): CommandResult {
    if (!this.beforeChildren) {
      throw new Error("Cannot undo bake; original frame contents are missing.");
    }

    const frame = requireSceneNode(context.document, this.frameId);
    if (frame.type !== "frame") {
      throw new Error(`Node ${this.frameId} is not a frame.`);
    }

    frame.children.splice(0, frame.children.length, ...this.beforeChildren.map(child => structuredClone(child)));
    finalizeDocumentGeometry(context.document, [this.frameId, ...this.beforeChildIds, ...this.afterChildIds]);

    return {
      changedNodeIds: [...new Set([this.frameId, ...this.beforeChildIds, ...this.afterChildIds])],
      affectedBounds: [frame.bounds],
    };
  }

  redo(context: CommandContext): CommandResult {
    return this.execute(context);
  }
}

interface SceneNodeParent {
  type: "page" | "frame" | "group" | "boolean" | "component" | "componentVariant" | "instance" | "unknown";
  id: string;
}

interface ParentOrderSnapshot {
  parent: SceneNodeParent;
  beforeIds: string[];
  afterIds?: string[];
}

interface GroupSnapshot {
  parent: SceneNodeParent;
  nodeLocations: Array<{ parent: SceneNodeParent; index: number; node: SceneNode }>;
  nodeIds: string[];
  group: GroupNode | FrameNode;
}

interface ComponentSnapshot {
  parent: SceneNodeParent;
  nodeLocations: Array<{ parent: SceneNodeParent; index: number; node: SceneNode }>;
  nodeIds: string[];
  component: ComponentNode;
}

interface UngroupSnapshot {
  parent: SceneNodeParent;
  index: number;
  group: GroupNode;
}

interface ReplaceSnapshot {
  parent: SceneNodeParent;
  children: SceneNode[];
  replacementIds: string[];
}

export function canGroupNodes(document: CurvaDocument, nodeIds: string[]): boolean {
  const normalizedNodeIds = normalizeTopLevelNodeIds(document, nodeIds);
  return normalizedNodeIds.length >= 2 && normalizedNodeIds.every(nodeId => findNodeLocation(document, nodeId));
}

export function canFrameNodes(document: CurvaDocument, nodeIds: string[]): boolean {
  const normalizedNodeIds = normalizeTopLevelNodeIds(document, nodeIds);
  return normalizedNodeIds.length >= 1 && normalizedNodeIds.every(nodeId => findNodeLocation(document, nodeId));
}

export function canCreateComponent(document: CurvaDocument, nodeIds: string[]): boolean {
  const normalizedNodeIds = normalizeTopLevelNodeIds(document, nodeIds);
  return normalizedNodeIds.length >= 1 && normalizedNodeIds.every(nodeId => {
    const location = findNodeLocation(document, nodeId);
    return location && location.node.type !== "component" && location.node.type !== "instance";
  });
}

export function canUngroupNodes(document: CurvaDocument, nodeIds: string[]): boolean {
  return nodeIds.some(nodeId => findSceneNodeInDocument(document, nodeId)?.type === "group");
}

export function canReplaceNodes(document: CurvaDocument, nodeIds: string[]): boolean {
  return nodeIds.length > 0 && findSharedParent(document, nodeIds) !== undefined;
}

export function createDuplicateNodeCommands(
  document: CurvaDocument,
  nodeIds: string[],
  offset: { x: number; y: number },
): { commands: InsertNodeCommand[]; nodeIds: string[] } {
  const normalizedNodeIds = normalizeTopLevelNodeIds(document, nodeIds);
  const locations = normalizedNodeIds.flatMap(nodeId => {
    const location = findNodeLocation(document, nodeId);
    return location ? [location] : [];
  });
  const insertedByParent = new Map<string, number>();
  const commands: InsertNodeCommand[] = [];
  const duplicatedNodeIds: string[] = [];

  for (const location of locations.sort((left, right) => parentKey(left.parent).localeCompare(parentKey(right.parent)) || left.index - right.index)) {
    const key = parentKey(location.parent);
    const insertedBefore = insertedByParent.get(key) ?? 0;
    const duplicatedNode = location.node.type === "component"
      ? createInstanceFromComponent(location.node, offset)
      : cloneSceneNodeWithFreshIds(location.node, { offset, renameRoot: true });
    commands.push(new InsertNodeCommand(location.parent.id, duplicatedNode, location.index + 1 + insertedBefore));
    duplicatedNodeIds.push(duplicatedNode.id);
    insertedByParent.set(key, insertedBefore + 1);
  }

  return { commands, nodeIds: duplicatedNodeIds };
}

function groupNodes(document: CurvaDocument, nodeIds: string[], groupId: string, containerType: "group" | "frame" = "group"): GroupSnapshot {
  const normalizedNodeIds = normalizeTopLevelNodeIds(document, nodeIds);
  if (normalizedNodeIds.length < (containerType === "frame" ? 1 : 2)) {
    throw new Error(containerType === "frame" ? "Select an object to frame." : "Select two or more objects to group.");
  }

  const locations = normalizedNodeIds.map(nodeId => {
    const location = findNodeLocation(document, nodeId);
    if (!location) {
      throw new Error(`Node ${nodeId} was not found.`);
    }
    return location;
  });
  const target = locations[0]!;
  const sameParentRemovalCountBeforeTarget = locations.filter(location => parentReferencesEqual(location.parent, target.parent) && location.index < target.index).length;
  const nodes = locations.map(location => structuredClone(location.node));
  const bounds = getNodesBounds(nodes);
  const group: GroupNode | FrameNode = containerType === "frame"
    ? {
        id: groupId,
        name: "Frame",
        type: "frame",
        visible: true,
        locked: false,
        transform: createIdentityTransform(),
        bounds,
        style: {
          ...createDefaultStyle("#000000"),
          fills: [],
          strokes: [],
          stroke: undefined,
        },
        cornerRadii: [0, 0, 0, 0],
        clipsContent: true,
        autoLayout: "off",
        flexLayout: createDefaultFrameFlexLayout(),
        children: nodes.map(node => structuredClone(node)),
      }
    : {
        id: groupId,
        name: "Group",
        type: "group",
        visible: true,
        locked: false,
        transform: createIdentityTransform(),
        bounds,
        style: createDefaultStyle("#000000"),
        children: nodes.map(node => structuredClone(node)),
      };

  for (const location of [...locations].sort((left, right) => parentKey(left.parent).localeCompare(parentKey(right.parent)) || right.index - left.index)) {
    location.children.splice(location.index, 1);
  }

  const shared = { parent: target.parent, children: requireParentChildren(document, target.parent) };
  const insertionIndex = Math.min(Math.max(0, target.index - sameParentRemovalCountBeforeTarget), shared.children.length);
  shared.children.splice(insertionIndex, 0, group);
  return {
    parent: shared.parent,
    nodeLocations: locations.map(location => ({
      parent: location.parent,
      index: location.index,
      node: structuredClone(location.node),
    })),
    nodeIds: normalizedNodeIds,
    group: structuredClone(group),
  };
}

function createComponentFromNodes(document: CurvaDocument, nodeIds: string[], componentId: string): ComponentSnapshot {
  const normalizedNodeIds = normalizeTopLevelNodeIds(document, nodeIds);
  if (normalizedNodeIds.length < 1) {
    throw new Error("Select at least one object to create a component.");
  }

  const locations = normalizedNodeIds.map(nodeId => {
    const location = findNodeLocation(document, nodeId);
    if (!location) {
      throw new Error(`Node ${nodeId} was not found.`);
    }
    return location;
  });
  const target = locations[0]!;
  const sameParentRemovalCountBeforeTarget = locations.filter(location => parentReferencesEqual(location.parent, target.parent) && location.index < target.index).length;
  const nodes = locations.map(location => structuredClone(location.node));
  const bounds = getNodesBounds(nodes);
  const defaultVariant: ComponentVariantNode = {
    id: createId("variant"),
    name: "Default",
    type: "componentVariant",
    visible: true,
    locked: false,
    transform: createIdentityTransform(),
    bounds,
    style: createDefaultStyle("#000000"),
    children: nodes.map(node => structuredClone(node)),
  };
  const component: ComponentNode = {
    id: componentId,
    name: nodes.length === 1 ? nodes[0]!.name : `Set ${getAllComponents(document).length + 1}`,
    type: "component",
    visible: true,
    locked: false,
    transform: createIdentityTransform(),
    bounds,
    style: createDefaultStyle("#000000"),
    children: [defaultVariant],
  };

  for (const location of [...locations].sort((left, right) => parentKey(left.parent).localeCompare(parentKey(right.parent)) || right.index - left.index)) {
    location.children.splice(location.index, 1);
  }

  const shared = { parent: target.parent, children: requireParentChildren(document, target.parent) };
  const insertionIndex = Math.min(Math.max(0, target.index - sameParentRemovalCountBeforeTarget), shared.children.length);
  shared.children.splice(insertionIndex, 0, component);
  return {
    parent: shared.parent,
    nodeLocations: locations.map(location => ({
      parent: location.parent,
      index: location.index,
      node: structuredClone(location.node),
    })),
    nodeIds: normalizedNodeIds,
    component: structuredClone(component),
  };
}

function ungroupNode(document: CurvaDocument, nodeId: string): UngroupSnapshot[] {
  const removal = removeSceneNode(document, nodeId);
  if (removal.node.type !== "group") {
    const children = requireParentChildren(document, removal.parent);
    children.splice(removal.index, 0, removal.node);
    return [];
  }

  const children = requireParentChildren(document, removal.parent);
  children.splice(removal.index, 0, ...removal.node.children.map(child => structuredClone(child)));
  return [{ parent: removal.parent, index: removal.index, group: structuredClone(removal.node) }];
}

function moveNodesInTree(document: CurvaDocument, nodeIds: string[], target: NodeTreeDropTarget): void {
  if (isComponentInstanceDropParent(document, target.parentId)) {
    throw new Error("Cannot move layers inside a component instance.");
  }

  const movingIds = new Set(nodeIds);
  if (movingIds.has(target.parentId)) {
    throw new Error("Cannot move layers inside themselves.");
  }

  for (const nodeId of nodeIds) {
    if (isNodeDescendantOf(document, target.parentId, nodeId)) {
      throw new Error("Cannot move layers inside their own children.");
    }
  }

  const removals = nodeIds.map(nodeId => {
    const location = findNodeLocation(document, nodeId);
    if (!location) {
      throw new Error(`Node ${nodeId} was not found.`);
    }
    return location;
  });
  const targetParent = targetParentReference(target);
  const movedNodes = nodeIds.map(nodeId => {
    const location = removals.find(candidate => candidate.node.id === nodeId);
    if (!location) {
      throw new Error(`Node ${nodeId} was not found.`);
    }
    if (parentReferencesEqual(location.parent, targetParent.parent)) {
      return structuredClone(location.node);
    }
    return reparentNodePreserveWorldAppearance(document, nodeId, target.parentId);
  });
  const sameParentRemovalCountBeforeTarget = removals.filter(
    removal => parentReferencesEqual(removal.parent, targetParent.parent) && removal.index < target.index,
  ).length;

  for (const removal of [...removals].sort((left, right) => parentKey(left.parent).localeCompare(parentKey(right.parent)) || right.index - left.index)) {
    removal.children.splice(removal.index, 1);
  }

  const children = requireParentChildren(document, targetParent.parent);
  const insertionIndex = Math.min(Math.max(0, target.index - sameParentRemovalCountBeforeTarget), children.length);
  children.splice(insertionIndex, 0, ...movedNodes);
}

function normalizeTopLevelNodeIds(document: CurvaDocument, nodeIds: string[]): string[] {
  const requested = new Set(nodeIds);
  const normalized: string[] = [];
  for (const node of nodesInDocumentOrder(document)) {
    if (!requested.has(node.id)) {
      continue;
    }

    if (!hasRequestedAncestor(document, node.id, requested)) {
      normalized.push(node.id);
    }
  }
  return normalized;
}

function nodesInDocumentOrder(document: CurvaDocument): SceneNode[] {
  const nodes: SceneNode[] = [];
  for (const page of document.pages) {
    collectNodesInOrder(page.children, nodes);
  }
  return nodes;
}

function collectNodesInOrder(children: SceneNode[], nodes: SceneNode[]): void {
  for (const node of children) {
    nodes.push(node);
    const nestedChildren = getSceneNodeChildren(node);
    if (nestedChildren) {
      collectNodesInOrder(nestedChildren, nodes);
    }
  }
}

function hasRequestedAncestor(document: CurvaDocument, nodeId: string, requested: Set<string>): boolean {
  const path = parentPathForNode(document, nodeId);
  return path.some(parent => requested.has(parent.id));
}

function isNodeDescendantOf(document: CurvaDocument, nodeId: string, ancestorId: string): boolean {
  return parentPathForNode(document, nodeId).some(parent => parent.id === ancestorId);
}

function parentPathForNode(document: CurvaDocument, nodeId: string): SceneNodeParent[] {
  for (const page of document.pages) {
    const path = findParentPathInChildren(page.children, nodeId, [{ type: "page", id: page.id }]);
    if (path) {
      return path;
    }
  }
  return [];
}

function findParentPathInChildren(children: SceneNode[], nodeId: string, path: SceneNodeParent[]): SceneNodeParent[] | undefined {
  for (const node of children) {
    if (node.id === nodeId) {
      return path;
    }

    const nestedChildren = getSceneNodeChildren(node);
    if (!nestedChildren) {
      continue;
    }

    const nestedPath = findParentPathInChildren(nestedChildren, nodeId, [...path, { type: containerParentType(node), id: node.id }]);
    if (nestedPath) {
      return nestedPath;
    }
  }

  return undefined;
}

function targetParentReference(target: NodeTreeDropTarget): { parent: SceneNodeParent } {
  const parent: SceneNodeParent = { type: target.parentType, id: target.parentId };
  return { parent };
}

function findNodeLocation(
  document: CurvaDocument,
  nodeId: string,
): { node: SceneNode; index: number; parent: SceneNodeParent; children: SceneNode[] } | undefined {
  for (const page of document.pages) {
    const location = findNodeLocationInChildren(page.children, nodeId, { type: "page", id: page.id });
    if (location) {
      return location;
    }
  }
  return undefined;
}

function findNodeLocationInChildren(
  children: SceneNode[],
  nodeId: string,
  parent: SceneNodeParent,
): { node: SceneNode; index: number; parent: SceneNodeParent; children: SceneNode[] } | undefined {
  const index = children.findIndex(node => node.id === nodeId);
  if (index >= 0) {
    return { node: children[index]!, index, parent, children };
  }

  for (const node of children) {
    const nestedChildren = getSceneNodeChildren(node);
    if (!nestedChildren) {
      continue;
    }

    const location = findNodeLocationInChildren(nestedChildren, nodeId, { type: containerParentType(node), id: node.id });
    if (location) {
      return location;
    }
  }

  return undefined;
}

function parentReferencesEqual(left: SceneNodeParent, right: SceneNodeParent): boolean {
  return left.type === right.type && left.id === right.id;
}

function parentKey(parent: SceneNodeParent): string {
  return `${parent.type}:${parent.id}`;
}

function requireSceneNode(document: CurvaDocument, nodeId: string): SceneNode {
  const node = findSceneNodeInDocument(document, nodeId);
  if (!node) {
    throw new Error(`Node ${nodeId} was not found.`);
  }

  return node;
}

function removeSceneNode(document: CurvaDocument, nodeId: string): { node: SceneNode; index: number; parent: SceneNodeParent } {
  for (const page of document.pages) {
    const pageRemoval = removeFromChildren(page.children, nodeId, { type: "page", id: page.id });
    if (pageRemoval) {
      return pageRemoval;
    }
  }

  throw new Error(`Node ${nodeId} was not found.`);
}

function removeFromChildren(
  children: SceneNode[],
  nodeId: string,
  parent: SceneNodeParent,
): { node: SceneNode; index: number; parent: SceneNodeParent } | undefined {
  const index = children.findIndex(node => node.id === nodeId);
  if (index >= 0) {
    const [node] = children.splice(index, 1);
    return { node: node!, index, parent };
  }

  for (const node of children) {
    const childList = getSceneNodeChildren(node);
    if (!childList) {
      continue;
    }

    const removal = removeFromChildren(childList, nodeId, { type: containerParentType(node), id: node.id });
    if (removal) {
      return removal;
    }
  }

  return undefined;
}

function findParentsWithNodes(document: CurvaDocument, nodeIds: Set<string>): Array<{ parent: SceneNodeParent; children: SceneNode[] }> {
  const parents: Array<{ parent: SceneNodeParent; children: SceneNode[] }> = [];
  for (const page of document.pages) {
    collectParentWithNodes(page.children, nodeIds, { type: "page", id: page.id }, parents);
  }

  return parents;
}

function collectParentWithNodes(
  children: SceneNode[],
  nodeIds: Set<string>,
  parent: SceneNodeParent,
  parents: Array<{ parent: SceneNodeParent; children: SceneNode[] }>,
): void {
  if (children.some(node => nodeIds.has(node.id))) {
    parents.push({ parent, children });
  }

  for (const node of children) {
    const childList = getSceneNodeChildren(node);
    if (childList) {
      collectParentWithNodes(childList, nodeIds, { type: containerParentType(node), id: node.id }, parents);
    }
  }
}

function findSharedParent(document: CurvaDocument, nodeIds: string[]): { parent: SceneNodeParent; children: SceneNode[] } | undefined {
  const selected = new Set(nodeIds);
  const parents = findParentsWithNodes(document, selected).filter(({ children }) => children.filter(node => selected.has(node.id)).length > 0);
  return parents.find(({ children }) => nodeIds.every(nodeId => children.some(node => node.id === nodeId)));
}

function reorderChildren(children: SceneNode[], nodeIds: Set<string>, placement: ReorderPlacement): void {
  if (placement === "front" || placement === "back") {
    const selected = children.filter(node => nodeIds.has(node.id));
    const rest = children.filter(node => !nodeIds.has(node.id));
    children.splice(0, children.length, ...(placement === "front" ? [...rest, ...selected] : [...selected, ...rest]));
    return;
  }

  if (placement === "forward") {
    for (let index = children.length - 2; index >= 0; index -= 1) {
      if (nodeIds.has(children[index]!.id) && !nodeIds.has(children[index + 1]!.id)) {
        [children[index], children[index + 1]] = [children[index + 1]!, children[index]!];
      }
    }
    return;
  }

  for (let index = 1; index < children.length; index += 1) {
    if (nodeIds.has(children[index]!.id) && !nodeIds.has(children[index - 1]!.id)) {
      [children[index - 1], children[index]] = [children[index]!, children[index - 1]!];
    }
  }
}

function restoreChildrenOrder(children: SceneNode[], orderedIds: string[]): void {
  const order = new Map(orderedIds.map((id, index) => [id, index]));
  children.sort((left, right) => (order.get(left.id) ?? Number.MAX_SAFE_INTEGER) - (order.get(right.id) ?? Number.MAX_SAFE_INTEGER));
}

function replaceNodeInParent(document: CurvaDocument, parent: SceneNodeParent, nodeId: string, replacement: SceneNode): void {
  const children = requireParentChildren(document, parent);
  const index = children.findIndex(node => node.id === nodeId);
  if (index < 0) {
    throw new Error(`Node ${nodeId} was not found.`);
  }
  children.splice(index, 1, replacement);
}

function detachedSceneNode(node: SceneNode): SceneNode {
  const clone = structuredClone(node);
  delete clone.componentSourceId;
  const children = getSceneNodeChildren(clone);
  if (children) {
    children.splice(0, children.length, ...children.map(child => detachedSceneNode(child)));
  }
  return clone;
}

function frameToGroup(node: Extract<SceneNode, { type: "frame" }>): GroupNode {
  const transform = structuredClone(node.transform);
  if (transform.rotation !== 0) {
    const origin = transformRotationOrigin(node.bounds, node.transform);
    transform.x = origin.x;
    transform.y = origin.y;
  }

  return {
    id: node.id,
    name: node.name,
    type: "group",
    visible: node.visible,
    locked: node.locked,
    transform,
    bounds: structuredClone(node.bounds),
    style: structuredClone(node.style),
    flexChild: node.flexChild ? structuredClone(node.flexChild) : undefined,
    constraints: node.constraints ? structuredClone(node.constraints) : undefined,
    componentSourceId: node.componentSourceId,
    styleBindings: node.styleBindings ? structuredClone(node.styleBindings) : undefined,
    nameManuallyEdited: node.nameManuallyEdited,
    children: structuredClone(node.children),
  };
}

function groupToFrame(node: Extract<SceneNode, { type: "group" }>): FrameNode {
  return {
    id: node.id,
    name: node.name,
    type: "frame",
    visible: node.visible,
    locked: node.locked,
    transform: structuredClone(node.transform),
    bounds: structuredClone(node.bounds),
    style: structuredClone(node.style),
    flexChild: node.flexChild ? structuredClone(node.flexChild) : undefined,
    constraints: node.constraints ? structuredClone(node.constraints) : undefined,
    componentSourceId: node.componentSourceId,
    styleBindings: node.styleBindings ? structuredClone(node.styleBindings) : undefined,
    nameManuallyEdited: node.nameManuallyEdited,
    children: structuredClone(node.children),
    clipsContent: true,
    cornerRadii: [0, 0, 0, 0],
    autoLayout: "off",
    flexLayout: createDefaultFrameFlexLayout(),
  };
}

function selectedNodeBounds(document: CurvaDocument, nodeIds: string[]): Rect[] {
  return nodeIds.flatMap(nodeId => {
    const node = findSceneNodeInDocument(document, nodeId);
    return node ? [node.bounds] : [];
  });
}

function findSceneNodeInDocument(document: CurvaDocument, nodeId: string): SceneNode | undefined {
  for (const page of document.pages) {
    const node = findSceneNode(page.children, nodeId);
    if (node) {
      return node;
    }
  }

  return undefined;
}

function ensureMaskHasContainerParent(document: CurvaDocument, nodeId: string, groupId = createId("group")): void {
  const location = findNodeLocation(document, nodeId);
  if (!location || location.parent.type !== "page") {
    return;
  }

  const [node] = location.children.splice(location.index, 1);
  if (!node) {
    return;
  }

  const group: GroupNode = {
    id: groupId,
    name: "Mask group",
    type: "group",
    visible: true,
    locked: false,
    transform: createIdentityTransform(),
    bounds: { ...node.bounds },
    style: createDefaultStyle("#000000"),
    children: [node],
  };
  location.children.splice(location.index, 0, group);
}

function normalizeTopLevelMasks(document: CurvaDocument): void {
  for (const page of document.pages) {
    for (let index = 0; index < page.children.length; index += 1) {
      const node = page.children[index];
      if (!node?.isMask) {
        continue;
      }
      const group: GroupNode = {
        id: createId("group"),
        name: "Mask group",
        type: "group",
        visible: true,
        locked: false,
        transform: createIdentityTransform(),
        bounds: { ...node.bounds },
        style: createDefaultStyle("#000000"),
        children: [node],
      };
      page.children.splice(index, 1, group);
    }
  }
}

function requireParentChildren(document: CurvaDocument, parent: SceneNodeParent): SceneNode[] {
  if (parent.type === "page" || parent.type === "unknown") {
    for (const page of document.pages) {
      if (page.id === parent.id) {
        return page.children;
      }
    }
  }

  const container = findSceneNodeInDocument(document, parent.id);
  const children = container ? getSceneNodeChildren(container) : undefined;
  if (!children) {
    throw new Error(`Parent ${parent.id} was not found or cannot contain children.`);
  }

  return children;
}

function findSceneNode(nodes: SceneNode[], nodeId: string): SceneNode | undefined {
  for (const node of nodes) {
    if (node.id === nodeId) {
      return node;
    }

    const children = getSceneNodeChildren(node);
    const child = children ? findSceneNode(children, nodeId) : undefined;
    if (child) {
      return child;
    }
  }

  return undefined;
}

function containerParentType(node: SceneNode): "frame" | "group" | "boolean" | "component" | "componentVariant" | "instance" {
  if (node.type !== "frame" && node.type !== "group" && node.type !== "boolean" && node.type !== "component" && node.type !== "componentVariant" && node.type !== "instance") {
    throw new Error(`Node ${node.id} cannot contain children.`);
  }

  return node.type;
}

function getNodesBounds(nodes: SceneNode[]): Rect {
  const left = Math.min(...nodes.map(node => node.bounds.x));
  const top = Math.min(...nodes.map(node => node.bounds.y));
  const right = Math.max(...nodes.map(node => node.bounds.x + node.bounds.width));
  const bottom = Math.max(...nodes.map(node => node.bounds.y + node.bounds.height));
  return { x: left, y: top, width: right - left, height: bottom - top };
}

function refreshGroupBounds(document: CurvaDocument): void {
  for (const page of document.pages) {
    refreshGroupBoundsInChildren(page.children);
  }
}

export function finalizeDocumentGeometry(document: CurvaDocument, changedNodeIds?: Iterable<string>): void {
  const changed = changedNodeIds ? [...changedNodeIds] : undefined;
  if (changed) {
    refreshVectorNetworkBounds(document, changed);
  }
  if (changedNodeIds) {
    refreshBooleanNodesForChangedNodes(document, changed!);
  } else {
    refreshBooleanNodes(document);
  }
  refreshGroupBounds(document);
  layoutComponentVariants(document);
  // Keep auto-sized text (AUTO_WIDTH / AUTO_HEIGHT) in sync with its content
  // before laying out, so flex alignment centers on the true intrinsic size.
  settleAutoLayout(document, changed, []);
  // Instances inherit their geometry from the variant, then re-apply overrides.
  // Two cases need a second settle pass: a text override changes the intrinsic
  // size of a label (hug containers must reflow around it), and a resized reflow
  // instance rebuilds at the variant's natural child sizes and needs its root
  // flex frame re-laid-out to redistribute the extra space (instead of scaling).
  const relayoutIds = refreshComponentInstances(document, changed);
  if (relayoutIds.length > 0) {
    settleAutoLayout(document, changed, relayoutIds);
  }
  refreshGroupBounds(document);
}

/**
 * Remeasures auto-sized text and re-applies auto-layout in one pass.
 *
 * Any text whose bounds changed is treated as a layout input so its container
 * re-flows even when the text id wasn't in the original change set. `extra`
 * lets callers force specific nodes (e.g. overridden instance text) into the
 * incremental change set.
 */
function settleAutoLayout(document: CurvaDocument, changed: string[] | undefined, extra: string[]): void {
  const remeasuredTextIds = refreshTextIntrinsicBounds(document);
  const layoutChangedIds = changed
    ? [...new Set([...changed, ...extra, ...remeasuredTextIds])]
    : undefined;
  applyAutoLayoutToDocument(document, { changedNodeIds: layoutChangedIds, applyBounds: applyBoundsToNode });
  layoutComponentVariants(document);
}

function refreshVectorNetworkBounds(document: CurvaDocument, changedNodeIds: Iterable<string>): void {
  const nodesById = new Map(nodesInDocumentOrder(document).map(node => [node.id, node] as const));
  for (const nodeId of changedNodeIds) {
    const node = nodesById.get(nodeId);
    if (node?.type === "vectorNetwork") {
      node.bounds = getVectorNetworkBounds(node.network);
    }
  }
}

function refreshGroupBoundsInChildren(children: SceneNode[]): void {
  for (const node of children) {
    const nestedChildren = getSceneNodeChildren(node);
    if (nestedChildren) {
      refreshGroupBoundsInChildren(nestedChildren);
    }

    if ((node.type === "group" || node.type === "componentVariant" || node.type === "instance") && node.children.length > 0) {
      node.bounds = getNodesBounds(node.children);
    }
  }
}

function layoutComponentVariants(document: CurvaDocument): void {
  for (const component of nodesInDocumentOrder(document).filter((node): node is ComponentNode => node.type === "component")) {
    const variants = componentVariants(component);
    if (variants.length === 0) {
      continue;
    }

    let y = component.bounds.y;
    const x = component.bounds.x;
    for (const variant of variants) {
      if (variant.children.length > 0) {
        variant.bounds = getNodesBounds(variant.children);
      }
      moveVariantTo(variant, x, y);
      y += variant.bounds.height + COMPONENT_VARIANT_GAP;
    }
    component.bounds = getNodesBounds(variants) ?? component.bounds;
  }
}

function moveVariantTo(variant: ComponentVariantNode, x: number, y: number): void {
  const dx = x - variant.bounds.x;
  const dy = y - variant.bounds.y;
  if (dx === 0 && dy === 0) {
    return;
  }

  const nextBounds = { ...variant.bounds, x, y };
  applyBoundsToNode(variant, nextBounds);
}

function getComponentInstances(document: CurvaDocument, componentId: string): ComponentInstanceNode[] {
  return nodesInDocumentOrder(document).filter((node): node is ComponentInstanceNode => node.type === "instance" && node.componentId === componentId);
}
